import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { ShoppingBag, ArrowLeft, CheckCircle, Tag, Zap } from 'lucide-react';
import { useProducts } from '../context/ProductContext';

const ProductDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const { products, loadingProducts } = useProducts();

  if (loadingProducts) return <div style={{ textAlign: 'center', padding: '5rem' }}>Loading...</div>;

  const product = products.find(p => p.id === parseInt(id));

  if (!product) {
    return (
      <div className="glass-panel animate-slide-up" style={{ textAlign: 'center', padding: '4rem', maxWidth: '600px', margin: '4rem auto' }}>
        <h2>Product not found</h2>
        <p style={{ color: 'var(--text-muted)', marginBottom: '2rem' }}>The gear you're looking for doesn't exist.</p>
        <button onClick={() => navigate('/')} className="btn btn-primary">Return Home</button>
      </div>
    );
  }

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;500;600;700;800&display=swap');

        .pd-page {
          font-family: 'Sora', sans-serif;
          max-width: 1160px;
          margin: 0 auto;
          box-sizing: border-box;
          animation: pdSlideUp 0.5s cubic-bezier(0.22,1,0.36,1) both;
        }

        @keyframes pdSlideUp {
          from { opacity: 0; transform: translateY(28px); }
          to   { opacity: 1; transform: translateY(0); }
        }

        /* ── Back button ── */
        .pd-back {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          background: transparent;
          border: none;
          color: var(--text-muted, #94a3b8);
          font-family: 'Sora', sans-serif;
          font-size: 0.9rem;
          font-weight: 600;
          cursor: pointer;
          padding: 0.5rem 0;
          margin-bottom: 1.75rem;
          letter-spacing: 0.2px;
          transition: color 0.18s;
        }
        .pd-back:hover { color: var(--text-main, #f1f5f9); }

        /* ── Main card ── */
        .pd-card {
          background: var(--glass-panel, rgba(255,255,255,0.07));
          backdrop-filter: blur(20px);
          border: 1px solid rgba(255,255,255,0.12);
          border-radius: 28px;
          overflow: hidden;
          display: grid;
          grid-template-columns: 1fr 1fr;
          min-height: 560px;
          box-shadow: 0 24px 72px rgba(0,0,0,0.22);
        }

        /* ── Image pane ── */
        .pd-image-pane {
          position: relative;
          overflow: hidden;
          background: rgba(0,0,0,0.15);
          min-height: 420px;
        }

        .pd-image-pane img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
          transition: transform 0.6s ease;
        }

        .pd-image-pane:hover img {
          transform: scale(1.04);
        }

        .pd-category-badge {
          position: absolute;
          top: 20px;
          left: 20px;
          background: rgba(0,0,0,0.45);
          backdrop-filter: blur(12px);
          border: 1px solid rgba(255,255,255,0.18);
          color: #e2e8f0;
          padding: 0.4rem 1rem;
          border-radius: 50px;
          font-size: 0.78rem;
          font-weight: 700;
          letter-spacing: 0.8px;
          text-transform: uppercase;
          display: flex;
          align-items: center;
          gap: 0.4rem;
        }

        /* ── Info pane ── */
        .pd-info-pane {
          display: flex;
          flex-direction: column;
          padding: 2.75rem 2.5rem;
          border-left: 1px solid rgba(255,255,255,0.08);
          box-sizing: border-box;
        }

        /* Top section grows to push buttons to bottom */
        .pd-info-top {
          flex: 1;
          display: flex;
          flex-direction: column;
        }

        .pd-title {
          font-size: 1.95rem;
          font-weight: 800;
          color: var(--text-main, #f1f5f9);
          line-height: 1.2;
          letter-spacing: -0.5px;
          margin: 0 0 0.85rem;
        }

        .pd-price-row {
          display: flex;
          align-items: center;
          gap: 1rem;
          margin-bottom: 1.5rem;
        }

        .pd-price {
          font-size: 1.65rem;
          font-weight: 800;
          background: linear-gradient(135deg, #b64c57ff, #b64c57ff);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          letter-spacing: -0.3px;
        }

        .pd-divider {
          height: 1px;
          background: rgba(255,255,255,0.08);
          margin-bottom: 1.5rem;
        }

        .pd-description {
          font-size: 0.97rem;
          color: var(--text-muted, #94a3b8);
          line-height: 1.8;
          margin-bottom: 2rem;
        }

        .pd-features-title {
          font-size: 0.78rem;
          font-weight: 700;
          letter-spacing: 1.2px;
          text-transform: uppercase;
          color: var(--text-muted, #94a3b8);
          margin-bottom: 1rem;
        }

        .pd-features-list {
          list-style: none;
          margin: 0 0 2rem;
          padding: 0;
          display: flex;
          flex-direction: column;
          gap: 0.65rem;
        }

        .pd-feature-item {
          display: flex;
          align-items: flex-start;
          gap: 0.65rem;
          font-size: 0.93rem;
          color: var(--text-main, #f1f5f9);
          line-height: 1.5;
        }

        .pd-feature-icon {
          flex-shrink: 0;
          margin-top: 2px;
        }

        /* ── Button bar — always at bottom ── */
        .pd-btn-bar {
          display: grid;
          grid-template-columns: 1fr 2fr;
          gap: 0.875rem;
          padding-top: 2rem;
          border-top: 1px solid rgba(255,255,255,0.08);
        }

        .pd-btn-outline {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          padding: 0.9rem 1rem;
          border-radius: 14px;
          border: 1.5px solid rgba(255,255,255,0.2);
          background: transparent;
          color: var(--text-main, #f1f5f9);
          font-family: 'Sora', sans-serif;
          font-size: 0.95rem;
          font-weight: 700;
          cursor: pointer;
          transition: background 0.18s, border-color 0.18s, transform 0.15s;
          white-space: nowrap;
        }

        .pd-btn-outline:hover {
          background: rgba(255,255,255,0.07);
          border-color: rgba(255,255,255,0.35);
          transform: translateY(-1px);
        }

        .pd-btn-primary {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.55rem;
          padding: 0.9rem 1.5rem;
          border-radius: 14px;
          border: none;
          background: linear-gradient(135deg, #3b82f6, #6366f1);
          color: #fff;
          font-family: 'Sora', sans-serif;
          font-size: 0.95rem;
          font-weight: 700;
          cursor: pointer;
          letter-spacing: 0.2px;
          box-shadow: 0 8px 24px rgba(99,102,241,0.4);
          transition: transform 0.18s, box-shadow 0.18s;
          white-space: nowrap;
        }

        .pd-btn-primary:hover {
          transform: translateY(-2px);
          box-shadow: 0 12px 32px rgba(99,102,241,0.52);
        }

        .pd-btn-primary:active,
        .pd-btn-outline:active { transform: translateY(0); }

        /* ── Tablet: slightly tighter ── */
        @media (max-width: 900px) {
          .pd-title { font-size: 1.6rem; }
          .pd-info-pane { padding: 2.25rem 2rem; }
        }

        /* ── Mobile: stack vertically ── */
        @media (max-width: 680px) {
          .pd-card {
            grid-template-columns: 1fr;
            border-radius: 22px;
          }

          .pd-image-pane {
            min-height: 280px;
            max-height: 340px;
          }

          .pd-info-pane {
            padding: 2rem 1.5rem;
            border-left: none;
            border-top: 1px solid rgba(255,255,255,0.08);
          }

          .pd-title { font-size: 1.45rem; }
          .pd-price { font-size: 1.4rem; }

          .pd-btn-bar {
            grid-template-columns: 1fr 1fr;
          }
        }

        @media (max-width: 420px) {
          .pd-btn-bar {
            grid-template-columns: 1fr;
          }
          .pd-btn-primary { order: -1; }
        }
      `}</style>

      <div className="pd-page">

        {/* Back */}
        <button className="pd-back" onClick={() => navigate(-1)}>
          <ArrowLeft size={17} /> Back to Products
        </button>

        {/* Card */}
        <div className="pd-card">

          {/* ── Image ── */}
          <div className="pd-image-pane">
            <img src={product.image} alt={product.name} />
            <div className="pd-category-badge">
              <Tag size={11} /> {product.category}
            </div>
          </div>

          {/* ── Info ── */}
          <div className="pd-info-pane">
            <div className="pd-info-top">

              <h1 className="pd-title">{product.name}</h1>

              <div className="pd-price-row">
                <span className="pd-price">₹{product.price.toFixed(2)}</span>
              </div>

              <div className="pd-divider" />

              <p className="pd-description">{product.fullDescription}</p>

              <p className="pd-features-title">Key Features</p>
              <ul className="pd-features-list">
                {product.features.map((feature, idx) => (
                  <li key={idx} className="pd-feature-item">
                    <CheckCircle size={17} color="var(--success, #10b981)" className="pd-feature-icon" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>

            </div>

            {/* ── Buttons — pinned to bottom ── */}
            <div className="pd-btn-bar">
              <button
                className="pd-btn-outline"
                onClick={() => { addToCart(product); navigate('/cart'); }}
              >
                <Zap size={16} /> Buy Now
              </button>
              <button
                className="pd-btn-primary"
                onClick={() => addToCart(product)}
              >
                <ShoppingBag size={17} /> Add to Cart
              </button>
            </div>

          </div>
        </div>
      </div>
    </>
  );
};

export default ProductDetails;